/**
 * Call Logs API - Log and track phone calls
 */
import apiClient from './index'

export interface CallLog {
  id: string
  direction: 'outbound' | 'inbound'
  call_type: 'call' | 'voicemail' | 'missed'
  phone_number: string
  duration_seconds: number
  duration_formatted: string
  started_at?: string
  ended_at?: string
  contact_id?: string
  contact_name?: string
  company_id?: string
  company_name?: string
  deal_id?: string
  outcome?: string
  disposition?: string
  subject?: string
  notes?: string
  recording_url?: string
  recording_duration_seconds?: number
  follow_up_date?: string
  follow_up_notes?: string
  user_id?: string
  created_at: string
  updated_at?: string
}

export interface CallLogStats {
  total_calls: number
  outbound_calls: number
  inbound_calls: number
  connected_calls: number
  connection_rate: number
  total_duration_seconds: number
  avg_duration_seconds: number
}

export interface CallOption {
  value: string
  label: string
}

export interface CallOptions {
  directions: CallOption[]
  call_types: CallOption[]
  outcomes: CallOption[]
  dispositions: CallOption[]
}

export interface CreateCallLogData {
  direction?: 'outbound' | 'inbound'
  call_type?: 'call' | 'voicemail' | 'missed'
  phone_number?: string
  duration_seconds?: number
  started_at?: string
  ended_at?: string
  contact_id?: string
  company_id?: string
  deal_id?: string
  outcome?: string
  disposition?: string
  subject?: string
  notes?: string
  recording_url?: string
  follow_up_date?: string
  follow_up_notes?: string
}

export interface UpdateCallLogData {
  direction?: 'outbound' | 'inbound'
  call_type?: 'call' | 'voicemail' | 'missed'
  phone_number?: string
  duration_seconds?: number
  outcome?: string
  disposition?: string
  subject?: string
  notes?: string
  follow_up_date?: string | null
  follow_up_notes?: string
}

export interface CallLogResponse {
  data: CallLog
}

export interface CallLogsResponse {
  data: CallLog[]
}

export interface CallStatsResponse {
  data: CallLogStats
}

export interface CallOptionsResponse {
  data: CallOptions
}

/**
 * Get call logs
 */
export async function getCallLogs(params?: {
  contact_id?: string
  company_id?: string
  deal_id?: string
  limit?: number
}): Promise<CallLog[]> {
  const searchParams = new URLSearchParams()
  if (params?.contact_id) searchParams.set('contact_id', params.contact_id)
  if (params?.company_id) searchParams.set('company_id', params.company_id)
  if (params?.deal_id) searchParams.set('deal_id', params.deal_id)
  if (params?.limit) searchParams.set('limit', String(params.limit))

  const queryString = searchParams.toString()
  const response = await apiClient.get<CallLogsResponse>(
    `/call-logs${queryString ? `?${queryString}` : ''}`
  )
  return response.data.data
}

/**
 * Get a single call log
 */
export async function getCallLog(logId: string): Promise<CallLog> {
  const response = await apiClient.get<CallLogResponse>(`/call-logs/${logId}`)
  return response.data.data
}

/**
 * Create a new call log
 */
export async function createCallLog(data: CreateCallLogData): Promise<CallLog> {
  const response = await apiClient.post<CallLogResponse>('/call-logs', data)
  return response.data.data
}

/**
 * Update a call log
 */
export async function updateCallLog(
  logId: string,
  data: UpdateCallLogData
): Promise<CallLog> {
  const response = await apiClient.put<CallLogResponse>(`/call-logs/${logId}`, data)
  return response.data.data
}

/**
 * Delete a call log
 */
export async function deleteCallLog(logId: string): Promise<void> {
  await apiClient.delete(`/call-logs/${logId}`)
}

/**
 * Get call statistics
 */
export async function getCallStats(days = 30): Promise<CallLogStats> {
  const response = await apiClient.get<CallStatsResponse>(`/call-logs/stats?days=${days}`)
  return response.data.data
}

/**
 * Get calls with pending follow-ups
 */
export async function getCallFollowUps(): Promise<CallLog[]> {
  const response = await apiClient.get<CallLogsResponse>('/call-logs/follow-ups')
  return response.data.data
}

/**
 * Get call logging options
 */
export async function getCallOptions(): Promise<CallOptions> {
  const response = await apiClient.get<CallOptionsResponse>('/call-logs/options')
  return response.data.data
}

// ============== Helpers ==============

/**
 * Format duration for display
 */
export function formatCallDuration(seconds: number): string {
  if (seconds === 0) return '0:00'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }
  return `${minutes}:${String(secs).padStart(2, '0')}`
}

/**
 * Get icon for call direction
 */
export function getDirectionIcon(direction: string): string {
  return direction === 'inbound' ? 'phone-incoming' : 'phone-outgoing'
}

/**
 * Get color for call outcome
 */
export function getOutcomeColor(outcome?: string): string {
  switch (outcome) {
    case 'connected':
      return 'green'
    case 'no_answer':
    case 'busy':
      return 'yellow'
    case 'voicemail':
      return 'blue'
    case 'wrong_number':
      return 'red'
    default:
      return 'gray'
  }
}

/**
 * Get label for call outcome
 */
export function getOutcomeLabel(outcome?: string): string {
  switch (outcome) {
    case 'connected':
      return 'Connected'
    case 'no_answer':
      return 'No Answer'
    case 'busy':
      return 'Busy'
    case 'voicemail':
      return 'Left Voicemail'
    case 'wrong_number':
      return 'Wrong Number'
    default:
      return 'Unknown'
  }
}
